import type { UploadOperation, UploadItem } from '@mimir/client-common/upload/UploadItem';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { getItemsWithDefaultFields } from '@mimir/web/api/globalClientGetItems';
import type { ClientWebsocket } from '@mimir/client-common/websockets/clientWebsocket';
import { doItemsPoll, SearchHitsPoller } from '@mimir/client-common/util/searchHitsPolling';
import type { LoadedItem } from '@mimir/common/dto/searchResultHit';
import type ClientMosHelper from '@mimir/common/mos/clientMosHelper';
import { assert } from '@mimir/common/util/invariant';
import type { FeatureFlag, FeatureSet } from '@mimir/common/util/featureSet';
import { QuashableAxios } from '@mimir/common/util/quashableHttp';
import Axios from 'axios';
import Vue from 'vue';
import Vuex, { type Store } from 'vuex';
import type { State } from './state';
import { DateHelper } from '@mimir/common/util/DateUtil';
import { getGlobalApiClient, globalBackendClient } from '@mimir/client-common/globals/backendClient';
import type { RenderDelegate } from '@mimir/common/dto/renderSequence';
import { portableClock } from '@mimir/common/async/timing';
import { standardItemEntryKeys } from '@mimir/common/dto/itemModelPrimitives';
import { checkStore } from './preventAdditionsToTheStore';
import type { QuickPreview } from '../model/quickPreviewOps';

/*VUE2*/ Vue.use(Vuex);

export const quashableAxios = new QuashableAxios(Axios);
const defaultFeatures: Partial<Record<FeatureFlag, boolean>> = {};

export type PresetSelectionUseCase = 'sequence_rendering' | 'subclip_tenant_config';


class ClientFeatureSet implements FeatureSet {
  constructor(public readonly storage: Storage, public readonly keyPrefix: string) {}

  private storageKey(feature: FeatureFlag) {
    return this.keyPrefix + feature;
  }

  /**
   * Feature flags are by default controlled by tenant configurations. However,
   * they can all be activated by activating the secret "experimental mode" of
   * the client using Ctrl+Shift+Alt+Click on burger menu.
   *
   * This behavior can be overridden feature by feature using the following
   * commands in the JavaScript console:
   *
   *  - features.enable('someFeatureName')
   *  - features.disable('someFeatureName')
   *  - features.clear('someFeatureName')
   *
   * Both "enable" and "disable" overrides both the "experimental mode" and the
   * tenant settings, while "clear" removes the override.
   *
   * Tenant configuration is persisted in DynamoDb, and ClientFeatureSet
   * overrides are persisted in local storage. The UI "experimental mode" is not
   * persisted across page reloads.
   */
  isFeatureEnabled(feature: FeatureFlag): boolean {
    const localValue = window.localStorage.getItem(this.storageKey(feature));
    if (localValue != null) {
      return localValue == 'true';
    }

    const store = getGlobalStore();
    if (store.state.experimental) {
      return true;
    }

    const tenantFlags = store.state.config.tenantConfig.featureFlags;
    if (tenantFlags) {
      const tenantValue = tenantFlags[feature];
      if (tenantValue != null) {
        return tenantValue;
      }
    }

    return defaultFeatures[feature] ?? false;
  }

  enable(feature: FeatureFlag) {
    window.localStorage.setItem(this.storageKey(feature), 'true');
  }

  disable(feature: FeatureFlag) {
    window.localStorage.setItem(this.storageKey(feature), 'false');
  }

  clear(feature: FeatureFlag) {
    window.localStorage.removeItem(this.storageKey(feature));
  }

  dump() {
    let i = 0;
    while (true) {
      const key = window.localStorage.key(i++);
      if (key == null) {
        break;
      }
      if (key.startsWith(this.keyPrefix)) {
        console.log(key.substr(this.keyPrefix.length) + ': ' + window.localStorage.getItem(key));
      }
    }
  }
}

export const features = new ClientFeatureSet(window.localStorage, 'feature-');

/*
  Contains Vuex store like state access patterns, with the exception that unlike
  Vuex, the state objects can be more than just plain JSON.
*/
export class AppState {

  /* --- State --- */

  mosHelper: ClientMosHelper | null = null;

  /**
   * Periodically polls for updates for search hits for which
   * there is additional info that we want to display that is either
   * not available or not fresh enough in the search response.
   */
  searchHitsPoller: SearchHitsPoller | null = null;

  clientWs: ClientWebsocket | null = null;
  features: FeatureSet = features;

  /**
   * Contains methods to return formatted dates in the configured timezone.
   * The timezone should be configured on the object at application startup.
   */
  dateHelper: DateHelper = new DateHelper();

  /**
   * We store upload progress for both multi-file and single file operations
   * here. This object is updated directly from the listeners instead of using
   * the traditional mutation pattern, to avoid a flood of mutations in the VueX
   * dev tools which I actually managed to crash.
   */
  trackedUploadProgress: Record<string, UploadOperation | null> = Vue.observable({});

  locallyExportingItems: { itemsMap: Record<string, UploadItem> } = Vue.observable({ itemsMap: {} });

  preferredUploadLocationId: string | null = null;

  preferredCentralConformKeldaId: string | null = null;

  preferredRenderDelegate: RenderDelegate | null = null;

  preferredCentralConformExportFormat: string | null = null;

  /**
   * The new version of currently opened item received from websocket message.
   */
  wsNewVersion: { value: LoadedItem | null } = Vue.observable({ value: null });

  /**
   * Used to prevent handling websocket 'item_version_change' message.
   */
  wsNewVersionWarningLock: boolean = false;

  preferredRenderingPresetId: Partial<Record<PresetSelectionUseCase, string>> = { };

  /**
   * Indicates whether audio tracks are currently visible in the video player.
   * This is used to toggle the visibility of the audio track controls.
   * Controls the `showAudioTracks` variable in VideoPlayerAndTimeline.vue.
   */
  audioTracksVisibility: boolean = false;

  /**
   * Represents the mute states of audio tracks in the video player.
   * This is used to restore the mute states when toggling audio track visibility.
   * For example: [true, false, true] means the first and third audio tracks are muted.
   */
  audioTracksMuteStates: boolean[] = [];

  runFnOnLayoutLoad: (() => any) | null = null;

  /* --- Mutations --- */

  putUploadProgress(itemId: string, operationProgress: UploadOperation | null) {
    Vue.set(this.trackedUploadProgress, itemId, operationProgress);
  }

  addLocallyExportingItem(item: UploadItem) {
    Vue.set(this.locallyExportingItems.itemsMap, item.id, item);
  }

  removeLocallyExportingItem(item: UploadItem) {
    Vue.delete(this.locallyExportingItems.itemsMap, item.id);
  }

  getPreferredCentralConformExportFormat() {
    return this.preferredCentralConformExportFormat;
  }

  setPreferredCentralConformExportFormat(exportFormat: string) {
    this.preferredCentralConformExportFormat = exportFormat;
  }

  setPreferredUploadLocationId(locationId: string) {
    this.preferredUploadLocationId = locationId;
  }


  /**
   * Update the Kelda configuration ID that the user has selected when exporting
   * sequence centrally.
   */
  setPreferredCentralConformKeldaId(keldaId: string) {
    this.preferredCentralConformKeldaId = keldaId;
  }

  setPreferredMimirCutterRenderDelegate(delegate: RenderDelegate) {
    this.preferredRenderDelegate = delegate;
  }

  setWSNewVersion(val: LoadedItem | null) {
    this.wsNewVersion.value = val;
  }

  setWsNewVersionWarningLock(val: boolean) {
    this.wsNewVersionWarningLock = val;
  }

  setPreferredRenderingPresetId(useCase: PresetSelectionUseCase, presetId: string | null) {
    if (presetId !== null) {
      this.preferredRenderingPresetId[useCase] = presetId;
    } else {
      delete this.preferredRenderingPresetId[useCase];
    }
  }

  setAudioTracksVisibility(visible: boolean) {
    this.audioTracksVisibility = visible;
  }

  setAudioTracksMuteStates(muteStates: boolean[]) {
    this.audioTracksMuteStates = [...muteStates];
  }

  /* --- Actions --- */
  prepareSearchHitsPolling(quickPreview: QuickPreview) {
    if (this.searchHitsPoller) {
      return;
    }
    this.searchHitsPoller = new SearchHitsPoller(() => itemProgressPoll(quickPreview), portableClock());
    this.searchHitsPoller.loop();
  }

  /* --- Getters --- */

  /**
   * Returns the new version of currently opened item from websocket message.
   * @returns LoadedItem
   */
  getWsNewVersion() {
    return this.wsNewVersion.value;
  }

  /**
   * Prevents handling websocket 'item_version_change' message when the user himself change the version.
   * @returns boolean
   */
  isWsNewVersionLock() {
    return this.wsNewVersionWarningLock;
  }

  getUploadProgress(itemId: string): UploadOperation | null {
    if (!(itemId in this.trackedUploadProgress))
      Vue.set(this.trackedUploadProgress, itemId, null);

    return this.trackedUploadProgress[itemId];
  }

  getLocallyExportingItems() {
    return this.locallyExportingItems.itemsMap;
  }

  getPreferredUploadLocationId() {
    return this.preferredUploadLocationId;
  }

  /**
  * Returns the Kelda configuration ID that the user last selected as the target
  * render location in the last sequence export operation.
  */
  getPreferredCentralConformKeldaId() {
    return this.preferredCentralConformKeldaId;
  }

  getPreferredMimirCutterRenderDelegate() {
    return this.preferredRenderDelegate;
  }

  getPreferredRenderingPresetId(useCase: PresetSelectionUseCase | null): string | null {
    if (useCase === null) {
      return null;
    }
    return this.preferredRenderingPresetId[useCase] ?? null;
  }

  getAudioTracksVisibility(): boolean {
    return this.audioTracksVisibility;
  }

  getAudioTracksMuteStates(): boolean[] {
    return [...this.audioTracksMuteStates];
  }
}

export const appState = new AppState();

const itemProgressPoll = async (quickPreview: QuickPreview) => {
  if (document.hidden) {
    return;
  }
  try {
    const store = getGlobalStore();
    const { returnedItems, updatedPollList } = await doItemsPoll(
      getItemsWithDefaultFields,
      () => store.getters.allItemsToPoll,
    );
    store.commit('setItemsToPoll', updatedPollList);

    await store.dispatch('updateItemsFromPoll', { items: returnedItems, retrievedFields: standardItemEntryKeys, quickPreview });
  } catch (err) {
    logErrorAndShowToast('Could not poll items for progress.', err, 'ex in itemProgressPoll');
  }
};


/*
  Expose a runtime testing API to the browser JavaScript console.
*/
Object.assign(window, {
  setFailureRate: (likelihoodOfFailure: number) => {
    getGlobalApiClient().setFailureRate(likelihoodOfFailure);
    quashableAxios.setFailureRate(likelihoodOfFailure);
  },
  getGlobalApiClient,
  quashableAxios,
  appState,
  getGlobalStore,
  features
});

const winany: any = window;
winany.mimirGlobals = winany.mimirGlobals ?? {};
if (winany.mimirGlobals.backendClient) {
  console.error('Vuex store was reloaded. Access of the store/index module will now be broken, since it will return a different store instance than what the rest of the UI is using!');
} else {
  winany.mimirGlobals.backendClient = globalBackendClient;
}

let globalStore: Store<State> | undefined;

export function getGlobalStore(): Store<State> {
  assert(globalStore, 'Global store not initialized');
  return globalStore;
}

export function setGlobalStore(store: Store<State>) {
  assert(!globalStore, 'Global store already initialized');
  globalStore = store;
  checkStore(store);
}

